import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  ArrowLeft, FileText, Image, Video, Users, Calendar, 
  CheckCircle, Clock, TrendingUp, Instagram, X, ExternalLink, Play, Download, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

interface SubmissionDetail {
  id: string;
  title: string;
  description: string;
  status: string;
  submission_type: string;
  submitted_by_user_id: string;
  submitted_by_user_name: string;
  assets: Array<any>;  // Changed from single asset to array
  packaged_assets: Array<any>;  // Changed from single packaged_asset to array
  caption: string | null;
  collaborators: Array<{name: string; handle: string | null}>;
  collaborators_text: string | null;
  creative_type: string | null;  // NEW: Reel, Single Photo Post, Carousel, Video Post, Story
  suggested_hashtags: string | null;  // NEW: Hashtags/keywords
  proposed_posting_date: string | null;  // NEW: Proposed posting date
  tag: string | null;  // NEW: Optional tag for categorization
  agency_reviews: Array<any>;
  approvals: Array<any>;
  posting: any;
  metrics: any;
  timeline: Array<any>;
  rework_required: boolean;
  created_at: string;
  updated_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  'DRAFT': 'bg-gray-100 text-gray-800',
  'SUBMITTED': 'bg-blue-100 text-blue-800',
  'AGENCY_REVIEW': 'bg-blue-100 text-blue-800',
  'AGENCY_EDIT_REQUESTED': 'bg-orange-100 text-orange-800',
  'AGENCY_ACCEPTED': 'bg-green-100 text-green-800',
  'APPROVAL_1_PENDING': 'bg-yellow-100 text-yellow-800',
  'APPROVAL_1_APPROVED': 'bg-green-100 text-green-800',
  'PACKAGING': 'bg-purple-100 text-purple-800',
  'APPROVAL_2_PENDING': 'bg-yellow-100 text-yellow-800',
  'APPROVAL_2_APPROVED': 'bg-green-100 text-green-800',
  'REJECTED': 'bg-red-100 text-red-800',
  'POSTED': 'bg-indigo-100 text-indigo-800',
  'METRICS_SYNCED': 'bg-teal-100 text-teal-800',
};

const SubmissionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submission, setSubmission] = useState<SubmissionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'creative' | 'posting'>('overview');
  
  // Preview modals
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<any>(null);
  const [previewType, setPreviewType] = useState<'original' | 'packaged'>('original');
  const [previewUrl, setPreviewUrl] = useState<string>('');

  // Submit for approval
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Approve/Reject modals
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approveComment, setApproveComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Creative tab - Packaging
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [packageFile, setPackageFile] = useState<File | null>(null);
  const [packagePreview, setPackagePreview] = useState<string | null>(null);
  const [packageCaption, setPackageCaption] = useState('');
  const [packageCollaborators, setPackageCollaborators] = useState('');
  const [creativeType, setCreativeType] = useState('');
  const [suggestedHashtags, setSuggestedHashtags] = useState('');
  const [proposedPostingDate, setProposedPostingDate] = useState('');
  const [packageTag, setPackageTag] = useState('');
  const [useOriginalAsset, setUseOriginalAsset] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'confirmed' | 'error'>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Posting tab - Recording Instagram post
  const [instagramUrl, setInstagramUrl] = useState('');
  const [postedAt, setPostedAt] = useState('');
  const [postingStatus, setPostingStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [postingError, setPostingError] = useState<string | null>(null);

  // Caption editing for Marketing Head
  const [editingCaption, setEditingCaption] = useState(false);
  const [editedCaption, setEditedCaption] = useState('');
  const [savingCaption, setSavingCaption] = useState(false);

  // Collaborators editing for Marketing Head
  const [editingCollaborators, setEditingCollaborators] = useState(false);
  const [editedCollaborators, setEditedCollaborators] = useState('');
  const [savingCollaborators, setSavingCollaborators] = useState(false);

  // Proposed Posting Date editing for Marketing Head
  const [editingPostingDate, setEditingPostingDate] = useState(false);
  const [editedPostingDate, setEditedPostingDate] = useState('');
  const [savingPostingDate, setSavingPostingDate] = useState(false);

  // Agency Review modals (for Creative Agency users)
  const [showAgencyAcceptModal, setShowAgencyAcceptModal] = useState(false);
  const [showAgencyEditRequestModal, setShowAgencyEditRequestModal] = useState(false);
  const [agencyComments, setAgencyComments] = useState('');

  useEffect(() => {
    if (id) {
      fetchSubmission();
    }
  }, [id]);

  const fetchSubmission = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/submissions/${id}`);
      setSubmission(response.data);
      // Pre-populate caption if it exists
      if (response.data.caption) {
        setPackageCaption(response.data.caption);
      }
      // Pre-populate collaborators if it exists
      if (response.data.collaborators_text) {
        setPackageCollaborators(response.data.collaborators_text);
      }
      // Pre-populate creative type if it exists
      if (response.data.creative_type) {
        setCreativeType(response.data.creative_type);
      }
      // Pre-populate hashtags if it exists
      if (response.data.suggested_hashtags) {
        setSuggestedHashtags(response.data.suggested_hashtags);
      }
      // Pre-populate proposed posting date if it exists
      if (response.data.proposed_posting_date) {
        setProposedPostingDate(response.data.proposed_posting_date);
      }
      // Pre-populate tag if it exists
      if (response.data.tag) {
        setPackageTag(response.data.tag);
      }
      // Pre-populate posting data if it exists
      if (response.data.posting?.instagram_url) {
        setInstagramUrl(response.data.posting.instagram_url);
      }
      if (response.data.posting?.posted_at) {
        // Convert ISO string to datetime-local format
        const dt = new Date(response.data.posting.posted_at);
        const localDateTime = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 16);
        setPostedAt(localDateTime);
      } else if (response.data.status === 'APPROVAL_2_APPROVED') {
        // Prefill with current datetime for new posting
        const now = new Date();
        const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 16);
        setPostedAt(localDateTime);
      }
    } catch (error: any) {
      console.error('Failed to fetch submission:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="text-center py-12">
        <FileText className="mx-auto text-gray-400 mb-4" size={48} />
        <p className="text-gray-600">Submission not found</p>
      </div>
    );
  }

  const formatStatus = (status: string) => {
    const statusMap: Record<string, string> = {
      'DRAFT': 'Draft',
      'AGENCY_REVIEW': 'Under Agency Review',
      'AGENCY_EDIT_REQUESTED': 'Edits Requested by Agency',
      'AGENCY_ACCEPTED': 'Accepted by Agency',
      'APPROVAL_1_PENDING': 'Pending Step 1 Approval',
      'APPROVAL_1_APPROVED': 'Step 1 Approved',
      'APPROVAL_2_PENDING': 'Pending Step 2 Approval',
      'APPROVAL_2_APPROVED': 'Step 2 Approved',
      'REJECTED': 'Rejected',
      'POSTED': 'Posted',
      'METRICS_SYNCED': 'Metrics Synced',
    };
    return statusMap[status] || status.replace(/_/g, ' ');
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handlePreview = async (asset: any, type: 'original' | 'packaged') => {
    setPreviewAsset(asset);
    setPreviewType(type);
    
    // Fetch the signed URL
    const url = await getPreviewUrl(asset, type);
    setPreviewUrl(url);
    
    if (asset.type === 'video') {
      setVideoPreviewOpen(true);
    } else {
      setImagePreviewOpen(true);
    }
  };

  const handleDownload = async (type: 'original' | 'packaged', asset: any) => {
    try {
      const url = await getPreviewUrl(asset, type);
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = asset.original_filename || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download:', error);
      alert('Failed to download file');
    }
  };

  const getPreviewUrl = async (asset: any, type: 'original' | 'packaged') => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/submissions/${id}/asset/download-url?asset_type=${type}`
      );
      return response.data.url;
    } catch (error) {
      console.error('Failed to get preview URL:', error);
      // Fallback to placeholder on error
      if (asset.type === 'video') {
        return 'https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';
      }
      return `https://via.placeholder.com/800x600/4A90E2/FFFFFF?text=${encodeURIComponent(asset.original_filename)}`;
    }
  };

  const openInNewTab = async (asset: any) => {
    const url = await getPreviewUrl(asset, previewType);
    window.open(url, '_blank');
  };

  const handleSubmitForApproval = async () => {
    try {
      setSubmitting(true);
      await axios.post(`${API}/submissions/${id}/submit`);
      setShowSubmitConfirm(false);
      // Refresh submission data
      await fetchSubmission();
      
      // Switch to Overview tab after submission
      setActiveTab('overview');
      
      alert('Submission successfully sent for approval!');
    } catch (error: any) {
      console.error('Failed to submit:', error);
      alert(error.response?.data?.detail || 'Failed to submit for approval');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!submission) return;
    
    const step = submission.status === 'APPROVAL_1_PENDING' ? 1 : 2;
    
    try {
      setProcessing(true);
      await axios.post(`${API}/submissions/${id}/approve`, {
        step,
        comment: approveComment || undefined
      });
      setShowApproveModal(false);
      setApproveComment('');
      await fetchSubmission();
      alert('Submission approved successfully!');
    } catch (error: any) {
      console.error('Failed to approve:', error);
      alert(error.response?.data?.detail || 'Failed to approve submission');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!submission || !rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    
    const step = submission.status === 'APPROVAL_1_PENDING' ? 1 : 2;
    
    try {
      setProcessing(true);
      await axios.post(`${API}/submissions/${id}/reject`, {
        step,
        reason: rejectReason
      });
      setShowRejectModal(false);
      setRejectReason('');
      await fetchSubmission();
      alert('Submission rejected successfully!');
    } catch (error: any) {
      console.error('Failed to reject:', error);
      alert(error.response?.data?.detail || 'Failed to reject submission');
    } finally {
      setProcessing(false);
    }
  };

  const handleResubmit = async () => {
    if (!submission) return;
    
    const isDirect = submission.submission_type === 'direct_to_head';
    
    // Determine the message based on status, submission type, and user role
    let message = 'Resubmit this submission?';
    if (submission.status === 'AGENCY_EDIT_REQUESTED') {
      message = 'Resubmit this submission to the Creative Agency for review?';
    } else if (submission.status === 'REJECTED') {
      if (isDirect && isMarketingExec) {
        // Direct submission rejected by Marketing Head - resubmit to Head
        message = 'Resubmit this submission to Head for review?';
      } else if (isCreativeAgency) {
        // Via Agency submission rejected - Creative Agency resubmits to Marketing Head
        message = 'Resubmit this submission to Marketing Head for approval?';
      } else {
        // Via Agency submission rejected - Marketing Exec resubmits to Creative Agency
        message = 'Resubmit this submission to Creative Agency for review?';
      }
    }
    
    if (!window.confirm(message)) {
      return;
    }
    
    try {
      setProcessing(true);
      // For AGENCY_EDIT_REQUESTED, use submit endpoint, otherwise use resubmit
      if (submission.status === 'AGENCY_EDIT_REQUESTED') {
        await axios.post(`${API}/submissions/${id}/submit`);
      } else {
        await axios.post(`${API}/submissions/${id}/resubmit`);
      }
      await fetchSubmission();
      
      // Switch to Overview tab after resubmission
      setActiveTab('overview');
      
      alert('Submission resubmitted successfully!');
    } catch (error: any) {
      console.error('Failed to resubmit:', error);
      alert(error.response?.data?.detail || 'Failed to resubmit submission');
    } finally {
      setProcessing(false);
    }
  };

  // Agency Review handlers
  const handleAgencyAccept = async () => {
    try {
      setProcessing(true);
      await axios.post(`${API}/submissions/${id}/agency/accept`, {
        comments: agencyComments || undefined
      });
      setShowAgencyAcceptModal(false);
      setAgencyComments('');
      await fetchSubmission();
      alert('Creative accepted successfully!');
    } catch (error: any) {
      console.error('Failed to accept:', error);
      alert(error.response?.data?.detail || 'Failed to accept creative');
    } finally {
      setProcessing(false);
    }
  };

  const handleAgencyRequestEdits = async () => {
    if (!agencyComments.trim()) {
      alert('Please provide comments explaining what edits are needed');
      return;
    }
    
    try {
      setProcessing(true);
      await axios.post(`${API}/submissions/${id}/agency/request-edits`, {
        comments: agencyComments
      });
      setShowAgencyEditRequestModal(false);
      setAgencyComments('');
      await fetchSubmission();
      alert('Edit request sent successfully!');
    } catch (error: any) {
      console.error('Failed to request edits:', error);
      alert(error.response?.data?.detail || 'Failed to request edits');
    } finally {
      setProcessing(false);
    }
  };

  // Packaging functions
  const handlePackageFileSelect = (selectedFile: File) => {
    setPackageFile(selectedFile);
    setUploadError(null);
    setUploadStatus('idle');

    // Create preview
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPackagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    } else if (selectedFile.type.startsWith('video/')) {
      const videoUrl = URL.createObjectURL(selectedFile);
      setPackagePreview(videoUrl);
    } else {
      setPackagePreview(null);
    }
  };

  const calculateChecksum = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  };

  const handlePackageUpload = async () => {
    const firstPackagedAsset = submission?.packaged_assets?.[0];
    
    // Check if file is required (when no existing packaged asset and not using original)
    if (!packageFile && !firstPackagedAsset && !useOriginalAsset) {
      setUploadError('Please select a file or check "Use original submission file"');
      return;
    }

    // Validate required fields
    if (!packageCaption || !packageCaption.trim()) {
      setUploadError('Caption is required');
      return;
    }
    
    if (!creativeType) {
      setUploadError('Creative Type is required');
      return;
    }
    
    if (!proposedPostingDate) {
      setUploadError('Proposed Posting Date is required');
      return;
    }

    setUploadStatus('uploading');
    setUploadError(null);
    setUploadProgress(10);

    try {
      // Use new backend proxy upload endpoint
      setUploadProgress(20);
      
      const formData = new FormData();
      
      // If using original asset, indicate it to backend
      if (useOriginalAsset && firstAsset) {
        formData.append('use_original_asset', 'true');
        formData.append('original_asset_id', firstAsset.id || firstAsset.storage_key);
      }
      // Only append file if a new one is selected and not using original
      else if (packageFile) {
        formData.append('files', packageFile);
      }
      
      // Add caption (required) and other fields
      formData.append('caption', packageCaption.trim());
      
      if (packageCollaborators) {
        formData.append('collaborators_text', packageCollaborators);
      }
      if (creativeType) {
        formData.append('creative_type', creativeType);
      }
      if (suggestedHashtags) {
        formData.append('suggested_hashtags', suggestedHashtags);
      }
      if (proposedPostingDate) {
        formData.append('proposed_posting_date', proposedPostingDate);
      }
      if (packageTag) {
        formData.append('tag', packageTag);
      }
      
      await axios.post(
        `${API}/submissions/${id}/packaged-asset/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = 20 + Math.round((progressEvent.loaded / progressEvent.total) * 75);
              setUploadProgress(progress);
            }
          }
        }
      );
      
      setUploadProgress(100);
      setUploadStatus('confirmed');
      
      // Wait for backend to process
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh submission data
      await fetchSubmission();
      alert('Packaged asset uploaded successfully! You can now submit for Step 2 approval.');
      
      // Reset form after short delay
      setTimeout(() => {
        setPackageFile(null);
        setPackagePreview(null);
        setUploadStatus('idle');
        setUploadProgress(0);
      }, 100);

    } catch (err: any) {
      console.error('Package upload error:', err);
      let errorMessage = 'Upload failed';
      
      if (err.response?.data?.detail) {
        // Handle if detail is an object (validation error) or string
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
        } else {
          errorMessage = JSON.stringify(err.response.data.detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setUploadError(errorMessage);
      setUploadStatus('error');
      setUploadProgress(0);
    }
  };

  const handleSubmitStep2 = async () => {
    if (!window.confirm('Submit this packaged submission for Step 2 approval?')) {
      return;
    }

    try {
      setProcessing(true);
      await axios.post(`${API}/submissions/${id}/submit-step2`);
      await fetchSubmission();
      
      // Switch to Overview tab after submission
      setActiveTab('overview');
      
      alert('Submission sent for Step 2 approval!');
    } catch (error: any) {
      console.error('Failed to submit for Step 2:', error);
      alert(error.response?.data?.detail || 'Failed to submit for Step 2 approval');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmitDirectToHead = async () => {
    if (!window.confirm('Submit this submission directly to Marketing Head for approval?')) {
      return;
    }

    try {
      setProcessing(true);
      await axios.post(`${API}/submissions/${id}/submit-direct-to-head`);
      await fetchSubmission();
      
      // Switch to Overview tab after submission
      setActiveTab('overview');
      
      alert('Submission sent directly to Marketing Head for approval!');
    } catch (error: any) {
      console.error('Failed to submit direct to head:', error);
      alert(error.response?.data?.detail || 'Failed to submit to Marketing Head');
    } finally {
      setProcessing(false);
    }
  };

  const handlePostingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!instagramUrl.trim()) {
      setPostingError('Instagram URL is required');
      return;
    }
    
    if (!postedAt) {
      setPostingError('Posted date and time is required');
      return;
    }
    
    // Validate URL format
    if (!instagramUrl.startsWith('https://www.instagram.com/p/') && 
        !instagramUrl.startsWith('https://www.instagram.com/reel/')) {
      setPostingError('Invalid Instagram URL. Must be a post or reel URL.');
      return;
    }
    
    // Convert datetime-local to ISO string
    const postedAtDate = new Date(postedAt);
    const now = new Date();
    
    // Check if future date
    if (postedAtDate > now) {
      setPostingError('Posted time cannot be in the future');
      return;
    }
    
    const postedAtISO = postedAtDate.toISOString();
    
    try {
      setPostingStatus('submitting');
      setPostingError(null);
      
      await axios.post(`${API}/submissions/${id}/posting`, {
        instagram_url: instagramUrl.trim(),
        posted_at: postedAtISO
      });
      
      setPostingStatus('success');
      await fetchSubmission();
      alert('Posting recorded successfully!');
      
    } catch (err: any) {
      console.error('Posting submission error:', err);
      setPostingError(err.response?.data?.detail || err.message || 'Failed to record posting');
      setPostingStatus('error');
    }
  };

  const handleCaptionSave = async () => {
    if (!editedCaption.trim()) {
      alert('Caption cannot be empty');
      return;
    }

    try {
      setSavingCaption(true);
      await axios.patch(`${API}/submissions/${id}/caption`, {
        caption: editedCaption
      });
      
      alert('Caption updated successfully!');
      setEditingCaption(false);
      await fetchSubmission();
    } catch (err: any) {
      console.error('Caption update error:', err);
      alert(err.response?.data?.detail || err.message || 'Failed to update caption');
    } finally {
      setSavingCaption(false);
    }
  };

  const handleCollaboratorsSave = async () => {
    if (!editedCollaborators.trim()) {
      alert('Collaborators cannot be empty');
      return;
    }

    try {
      setSavingCollaborators(true);
      await axios.patch(`${API}/submissions/${id}/collaborators`, {
        collaborators_text: editedCollaborators
      });
      
      alert('Collaborators updated successfully!');
      setEditingCollaborators(false);
      await fetchSubmission();
    } catch (err: any) {
      console.error('Collaborators update error:', err);
      alert(err.response?.data?.detail || err.message || 'Failed to update collaborators');
    } finally {
      setSavingCollaborators(false);
    }
  };

  const handlePostingDateSave = async () => {
    if (!editedPostingDate) {
      alert('Proposed Posting Date cannot be empty');
      return;
    }
    
    try {
      setSavingPostingDate(true);
      await axios.patch(`${API}/submissions/${id}/proposed-posting-date`, {
        proposed_posting_date: editedPostingDate
      });
      alert('Proposed Posting Date updated successfully!');
      setEditingPostingDate(false);
      await fetchSubmission();
    } catch (err: any) {
      console.error('Posting date update error:', err);
      alert('Failed to update posting date');
    } finally {
      setSavingPostingDate(false);
    }
  };

  // Check if user can approve/reject
  const canApproveReject = user?.roles?.includes('Marketing Head');
  const showApproveReject = canApproveReject && (
    submission?.status === 'APPROVAL_1_PENDING' || 
    submission?.status === 'APPROVAL_2_PENDING'
  );

  // Check if Marketing Head can edit caption (only during APPROVAL_2_PENDING)
  const canEditCaption = user?.roles?.includes('Marketing Head') && submission?.status === 'APPROVAL_2_PENDING';

  // Check if Marketing Head can edit collaborators (only during APPROVAL_2_PENDING)
  const canEditCollaborators = user?.roles?.includes('Marketing Head') && submission?.status === 'APPROVAL_2_PENDING';
  
  // Check if Marketing Head can edit Proposed Posting Date (only during APPROVAL_2_PENDING)
  const canEditPostingDate = user?.roles?.includes('Marketing Head') && submission?.status === 'APPROVAL_2_PENDING';

  // Check if user can edit (Marketing Executive/Creative Agency/Admin on specific statuses)
  const isMarketingExec = user?.roles?.includes('Marketing Executive');
  const isCreativeAgency = user?.roles?.includes('Creative Agency');
  const isAdmin = user?.roles?.includes('Admin');

  // Check if Creative Agency can review (Accept/Request Edits)
  const showAgencyReviewActions = isCreativeAgency && submission?.status === 'AGENCY_REVIEW';
  
  const canEdit = () => {
    const isDirect = submission?.submission_type === 'direct_to_head';
    const isOwner = submission?.submitted_by_user_id === user?.id;
    
    // Marketing Executive can edit own DRAFT or AGENCY_EDIT_REQUESTED
    // For DIRECT submissions, they can also edit REJECTED
    if (isMarketingExec && isOwner) {
      if (submission?.status === 'DRAFT' || submission?.status === 'AGENCY_EDIT_REQUESTED') {
        return true;
      }
      if (isDirect && submission?.status === 'REJECTED') {
        return true;
      }
    }
    
    // Creative Agency can edit own DRAFT or AGENCY_EDIT_REQUESTED
    // For DIRECT submissions, they can also edit REJECTED
    if (isCreativeAgency && isOwner) {
      if (submission?.status === 'DRAFT' || submission?.status === 'AGENCY_EDIT_REQUESTED') {
        return true;
      }
      if (isDirect && submission?.status === 'REJECTED') {
        return true;
      }
    }
    
    // Admin can edit DRAFT or AGENCY_EDIT_REQUESTED
    if (isAdmin && (submission?.status === 'DRAFT' || submission?.status === 'AGENCY_EDIT_REQUESTED')) {
      return true;
    }
    
    return false;
  };

  // Check if user can resubmit
  const canResubmit = () => {
    const isDirect = submission?.submission_type === 'direct_to_head';
    const isOwner = submission?.submitted_by_user_id === user?.id;
    const hasPackagedAsset = submission?.packaged_assets && submission.packaged_assets.length > 0;
    
    // Marketing Executive can resubmit own AGENCY_EDIT_REQUESTED
    // For DIRECT submissions, they can also resubmit REJECTED (but only if packaged asset exists)
    if (isMarketingExec && isOwner) {
      if (submission?.status === 'AGENCY_EDIT_REQUESTED') {
        return true;
      }
      if (isDirect && submission?.status === 'REJECTED' && hasPackagedAsset) {
        return true;
      }
    }
    
    // Creative Agency can resubmit own AGENCY_EDIT_REQUESTED or any REJECTED (via_agency)
    // For DIRECT submissions, they can resubmit their own REJECTED (but only if packaged asset exists)
    if (isCreativeAgency) {
      if (isOwner && submission?.status === 'AGENCY_EDIT_REQUESTED') {
        return true;
      }
      if (isDirect && isOwner && submission?.status === 'REJECTED' && hasPackagedAsset) {
        return true;
      }
      if (!isDirect && submission?.status === 'REJECTED') {
        return true;
      }
    }
    
    // Admin can resubmit any
    if (isAdmin && (submission?.status === 'REJECTED' || submission?.status === 'AGENCY_EDIT_REQUESTED')) {
      return true;
    }
    
    return false;
  };

  // Check if user can package
  // Creative Agency or Admin: on AGENCY_ACCEPTED or REJECTED (for via_agency submissions)
  // Marketing Executive: on DRAFT or REJECTED (for direct_to_head submissions - their own only)
  const canPackage = () => {
    const isDirect = submission?.submission_type === 'direct_to_head';
    const isOwner = submission?.submitted_by_user_id === user?.id;
    
    // Marketing Executive can package their own DRAFT or REJECTED direct submissions
    if (isDirect && isMarketingExec && isOwner && 
        (submission?.status === 'DRAFT' || submission?.status === 'REJECTED')) {
      return true;
    }
    
    // Creative Agency can package their own DRAFT or REJECTED direct submissions
    if (isDirect && isCreativeAgency && isOwner && 
        (submission?.status === 'DRAFT' || submission?.status === 'REJECTED')) {
      return true;
    }
    
    // Creative Agency/Admin can package via_agency submissions
    if ((user?.roles?.includes('Creative Agency') || user?.roles?.includes('Admin')) &&
        (submission?.status === 'AGENCY_ACCEPTED' || submission?.status === 'REJECTED')) {
      return true;
    }
    
    return false;
  };
  
  // Check if user can submit for Step 2 (only on AGENCY_ACCEPTED, not REJECTED)
  const canSubmitStep2 = (user?.roles?.includes('Creative Agency') || user?.roles?.includes('Admin')) &&
    submission?.status === 'AGENCY_ACCEPTED';

  // Check if user can record posting
  // For DIRECT submissions: Marketing Executive (owner), Creative Agency, or Admin can post
  // For VIA_AGENCY submissions: Only Creative Agency or Admin can post
  const canRecordPosting = () => {
    const isApprovedStatus = submission?.status === 'APPROVAL_2_APPROVED' || submission?.status === 'POSTED';
    if (!isApprovedStatus) return false;
    
    const isOwner = submission?.submitted_by_user_id === user?.id;
    const isDirect = submission?.submission_type === 'direct_to_head';
    
    if (isDirect && isMarketingExec && isOwner) {
      return true; // Marketing Exec can post their own DIRECT submissions
    }
    
    return user?.roles?.includes('Creative Agency') || user?.roles?.includes('Admin');
  };

  // Helper to get first asset for display (for backwards compatibility with single asset display)
  const firstAsset = submission?.assets && submission.assets.length > 0 ? submission.assets[0] : null;
  const firstPackagedAsset = submission?.packaged_assets && submission.packaged_assets.length > 0 ? submission.packaged_assets[0] : null;

  return (
    <div data-testid="submission-detail-page">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 lg:p-6 mb-4 lg:mb-6 border border-gray-200">
        <button
          onClick={() => navigate('/submissions')}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-3 sm:mb-4"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Submissions
        </button>
        
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              {submission.title}
            </h1>
            <p className="text-sm sm:text-base text-gray-600">{submission.description}</p>
          </div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
            <span className={`inline-flex items-center justify-center px-3 py-2 rounded-full text-sm font-medium ${STATUS_COLORS[submission.status]}`}>
              {formatStatus(submission.status)}
            </span>
            {canEdit() && (
              <button
                onClick={() => navigate(`/submissions/${id}/edit`)}
                className="min-h-[44px] px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-medium text-sm"
                data-testid="edit-button"
              >
                Edit
              </button>
            )}
            {canResubmit() && (
              <button
                onClick={handleResubmit}
                className="min-h-[44px] px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition font-medium text-sm"
                data-testid="resubmit-button"
                disabled={processing}
              >
                {processing 
                  ? 'Resubmitting...' 
                  : submission.status === 'AGENCY_EDIT_REQUESTED' 
                    ? 'Resubmit to Agency' 
                    : submission.submission_type === 'direct_to_head' && isMarketingExec && submission.status === 'REJECTED'
                      ? 'Resubmit to Head'
                      : isCreativeAgency && submission.status === 'REJECTED'
                        ? 'Resubmit for Step 2 Approval'
                        : 'Resubmit for Approval'}
              </button>
            )}
            {submission.status === 'DRAFT' && submission.submission_type === 'via_agency' && (
              <button
                onClick={() => setShowSubmitConfirm(true)}
                className="min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm"
                data-testid="submit-for-approval-button"
              >
                Submit to Agency
              </button>
            )}
            {submission.status === 'DRAFT' && submission.submission_type === 'direct_to_head' && (isMarketingExec || isCreativeAgency) && submission.submitted_by_user_id === user?.id && firstPackagedAsset && (
              <button
                onClick={handleSubmitDirectToHead}
                disabled={processing}
                className="min-h-[44px] px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium text-sm"
                data-testid="submit-direct-to-head-button"
              >
                {processing ? 'Submitting...' : 'Submit to Head'}
              </button>
            )}
            {canSubmitStep2 && firstPackagedAsset && (
              <button
                onClick={handleSubmitStep2}
                disabled={processing}
                className="min-h-[44px] px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium text-sm"
                data-testid="submit-step2-button"
              >
                {processing ? 'Submitting...' : 'Submit for Step 2 Approval'}
              </button>
            )}
            {showApproveReject && (
              <>
                <button
                  onClick={() => setShowApproveModal(true)}
                  className="min-h-[44px] px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium text-sm"
                  data-testid="approve-button"
                >
                  Approve
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  className="min-h-[44px] px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium text-sm"
                  data-testid="reject-button"
                >
                  Reject
                </button>
              </>
            )}
            {showAgencyReviewActions && (
              <>
                <button
                  onClick={() => setShowAgencyAcceptModal(true)}
                  className="min-h-[44px] px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium text-sm"
                  data-testid="agency-accept-button"
                >
                  Accept
                </button>
                <button
                  onClick={() => setShowAgencyEditRequestModal(true)}
                  className="min-h-[44px] px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition font-medium text-sm"
                  data-testid="agency-request-edits-button"
                >
                  Request Edits
                </button>
              </>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-600">
          <div className="flex items-center">
            <Users size={16} className="mr-1" />
            {submission.submitted_by_user_name}
          </div>
          <div className="flex items-center">
            <Calendar size={16} className="mr-1" />
            {new Date(submission.created_at).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex space-x-4 sm:space-x-8 px-3 sm:px-6 min-w-max" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="overview-tab"
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('timeline')}
              className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition ${
                activeTab === 'timeline'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="timeline-tab"
            >
              Timeline
            </button>
            {canPackage() && (
              <button
                onClick={() => setActiveTab('creative')}
                className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition ${
                  activeTab === 'creative'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid="creative-tab"
              >
                Creative
              </button>
            )}
            {canRecordPosting() && (
              <button
                onClick={() => setActiveTab('posting')}
                className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition ${
                  activeTab === 'posting'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid="posting-tab"
              >
                Posting
              </button>
            )}
          </nav>
        </div>

        <div className="p-3 sm:p-4 lg:p-6">
          {activeTab === 'overview' && (
            <div className="space-y-4 sm:space-y-6" data-testid="overview-content">
              {/* Rejection Reason Badge - Prompt 11 */}
              {submission.status === 'REJECTED' && submission.approvals && submission.approvals.length > 0 && (
                <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4" data-testid="rejection-badge">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={24} />
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-red-900 mb-2">Submission Rejected</h3>
                      {submission.approvals.filter(a => a.decision === 'REJECTED').map((rejection, index) => (
                        <div key={index} className="mb-3 last:mb-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="inline-flex px-2 py-1 bg-red-200 text-red-900 rounded text-xs font-medium">
                              Step {rejection.step} Rejection
                            </span>
                            <span className="text-xs text-red-700">
                              {new Date(rejection.decided_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-red-800 mb-2">
                            <span className="font-medium">Reason:</span> {rejection.reason}
                          </p>
                          {rejection.step === 1 && (
                            <div className="bg-red-100 rounded p-2 mt-2">
                              <p className="text-xs font-medium text-red-900 mb-1">📝 Next Steps:</p>
                              <ul className="text-xs text-red-800 space-y-1 ml-4 list-disc">
                                <li>Review the rejection reason above</li>
                                <li>Make necessary changes to your submission (title, description, or asset)</li>
                                <li>Click "Edit" button to update your submission</li>
                                <li>After editing, click "Resubmit for Approval" to send it back for review</li>
                              </ul>
                            </div>
                          )}
                          {rejection.step === 2 && (
                            <div className="bg-red-100 rounded p-2 mt-2">
                              <p className="text-xs font-medium text-red-900 mb-1">🎨 Next Steps for Creative Agency:</p>
                              <ul className="text-xs text-red-800 space-y-1 ml-4 list-disc">
                                <li>Review the rejection reason above</li>
                                <li>Go to the "Creative" tab to update packaged asset, caption, or collaborators</li>
                                <li>Re-upload the revised packaged asset</li>
                                <li>Click "Submit for Step 2 Approval" when ready</li>
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Agency Edit Request Badge - NEW */}
              {submission.status === 'AGENCY_EDIT_REQUESTED' && submission.agency_reviews && submission.agency_reviews.length > 0 && (
                <div className="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-4" data-testid="agency-edit-badge">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-orange-600 flex-shrink-0 mt-0.5" size={24} />
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-orange-900 mb-2">Edit Request from Creative Agency</h3>
                      {submission.agency_reviews.filter(r => r.decision === 'EDIT_REQUESTED').slice(-1).map((review, index) => (
                        <div key={index} className="mb-3 last:mb-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="inline-flex px-2 py-1 bg-orange-200 text-orange-900 rounded text-xs font-medium">
                              Agency Review
                            </span>
                            <span className="text-xs text-orange-700">
                              {new Date(review.reviewed_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-orange-800 mb-2">
                            <span className="font-medium">Feedback:</span> {review.comments}
                          </p>
                          <div className="bg-orange-100 rounded p-2 mt-2">
                            <p className="text-xs font-medium text-orange-900 mb-1">📝 Next Steps:</p>
                            <ul className="text-xs text-orange-800 space-y-1 ml-4 list-disc">
                              <li>Review the feedback from the Creative Agency above</li>
                              <li>Make the necessary changes to your submission</li>
                              <li>Click "Edit" button to update your submission if needed</li>
                              <li>Click "Resubmit for Approval" when ready to send it back to the agency</li>
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Asset Info */}
              {firstAsset && (
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Original Asset</h3>
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                      <div className="p-3 bg-blue-100 rounded-lg self-start">
                        {firstAsset.type === 'video' ? (
                          <Video className="text-blue-600" size={24} />
                        ) : (
                          <Image className="text-blue-600" size={24} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 break-words">{firstAsset.original_filename}</p>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1">
                          {firstAsset.mime_type} • {formatBytes(firstAsset.size_bytes)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 font-mono break-all">
                          {firstAsset.storage_key}
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <button
                          onClick={() => handlePreview(firstAsset, 'original')}
                          className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                          data-testid="preview-original-asset"
                        >
                          {firstAsset.type === 'video' ? (
                            <>
                              <Play size={16} className="mr-2" />
                              Play Video
                            </>
                          ) : (
                            <>
                              <Image size={16} className="mr-2" />
                              Preview
                            </>
                          )}
                        </button>
                        {isCreativeAgency && (
                          <button
                            onClick={() => handleDownload('original', firstAsset)}
                            className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
                            data-testid="download-original-asset"
                          >
                            <Download size={16} className="mr-2" />
                            Download
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Packaged Asset */}
              {firstPackagedAsset && (
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Packaged Asset</h3>
                  <div className="bg-green-50 rounded-lg p-3 sm:p-4 border border-green-200">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                      <div className="p-3 bg-green-100 rounded-lg self-start">
                        <CheckCircle className="text-green-600" size={24} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 break-words">{firstPackagedAsset.original_filename}</p>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1">
                          {firstPackagedAsset.mime_type} • {formatBytes(firstPackagedAsset.size_bytes)}
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <button
                          onClick={() => handlePreview(firstPackagedAsset, 'packaged')}
                          className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
                          data-testid="preview-packaged-asset"
                        >
                          {firstPackagedAsset.type === 'video' ? (
                            <>
                              <Play size={16} className="mr-2" />
                              Play Video
                            </>
                          ) : (
                            <>
                              <Image size={16} className="mr-2" />
                              Preview
                            </>
                          )}
                        </button>
                        {isCreativeAgency && (
                          <button
                            onClick={() => handleDownload('packaged', firstPackagedAsset)}
                            className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                            data-testid="download-packaged-asset"
                          >
                            <Download size={16} className="mr-2" />
                            Download
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Caption */}
              {(submission.caption || canEditCaption) && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Caption</h3>
                    {canEditCaption && !editingCaption && (
                      <button
                        onClick={() => {
                          setEditedCaption(submission.caption || '');
                          setEditingCaption(true);
                        }}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Edit Caption
                      </button>
                    )}
                  </div>
                  
                  {editingCaption ? (
                    <div className="space-y-3">
                      <textarea
                        value={editedCaption}
                        onChange={(e) => setEditedCaption(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter caption..."
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleCaptionSave}
                          disabled={savingCaption}
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          {savingCaption ? 'Saving...' : 'Save Caption'}
                        </button>
                        <button
                          onClick={() => setEditingCaption(false)}
                          disabled={savingCaption}
                          className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <p className="text-gray-700">{submission.caption || 'No caption added yet'}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Collaborators */}
              {(submission.collaborators.length > 0 || submission.collaborators_text || canEditCollaborators) && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Collaborators</h3>
                    {canEditCollaborators && !editingCollaborators && (
                      <button
                        onClick={() => {
                          setEditedCollaborators(submission.collaborators_text || '');
                          setEditingCollaborators(true);
                        }}
                        className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
                      >
                        Edit Collaborators
                      </button>
                    )}
                  </div>
                  
                  {editingCollaborators ? (
                    <div className="space-y-3">
                      <textarea
                        value={editedCollaborators}
                        onChange={(e) => setEditedCollaborators(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Enter collaborators..."
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleCollaboratorsSave}
                          disabled={savingCollaborators}
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          {savingCollaborators ? 'Saving...' : 'Save Collaborators'}
                        </button>
                        <button
                          onClick={() => setEditingCollaborators(false)}
                          disabled={savingCollaborators}
                          className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Display structured collaborators if any */}
                      {submission.collaborators.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {submission.collaborators.map((collab, idx) => (
                            <div key={idx} className="bg-purple-50 px-4 py-2 rounded-lg border border-purple-200">
                              <p className="font-medium text-gray-900">{collab.name}</p>
                              {collab.handle && <p className="text-sm text-purple-600">{collab.handle}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Display collaborators text */}
                      {submission.collaborators_text ? (
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <p className="text-gray-700 whitespace-pre-wrap">{submission.collaborators_text}</p>
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <p className="text-gray-500">No collaborators added yet</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Creative Type - NEW */}
              {submission.creative_type && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Creative Type</h3>
                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🎨</span>
                      <p className="text-indigo-900 font-medium">{submission.creative_type}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Suggested Hashtags - NEW */}
              {submission.suggested_hashtags && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Suggested Hashtags / Keywords</h3>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-blue-900 whitespace-pre-wrap">{submission.suggested_hashtags}</p>
                  </div>
                </div>
              )}

              {/* Proposed Posting Date - NEW */}
              {(submission.proposed_posting_date || canEditPostingDate) && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Proposed Posting Date</h3>
                    {canEditPostingDate && !editingPostingDate && (
                      <button
                        onClick={() => {
                          setEditedPostingDate(submission.proposed_posting_date || '');
                          setEditingPostingDate(true);
                        }}
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Edit Posting Date
                      </button>
                    )}
                  </div>
                  
                  {editingPostingDate ? (
                    <div className="space-y-3">
                      <input
                        type="date"
                        value={editedPostingDate}
                        onChange={(e) => setEditedPostingDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handlePostingDateSave}
                          disabled={savingPostingDate}
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          {savingPostingDate ? 'Saving...' : 'Save Posting Date'}
                        </button>
                        <button
                          onClick={() => setEditingPostingDate(false)}
                          disabled={savingPostingDate}
                          className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {submission.proposed_posting_date && (
                        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">📅</span>
                            <p className="text-green-900 font-medium">
                              {new Date(submission.proposed_posting_date).toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Tag - NEW Display */}
              {submission.tag && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Tag</h3>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🏷️</span>
                      <p className="text-blue-900 font-medium">
                        {submission.tag}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Posting Info */}
              {submission.posting.instagram_url && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Instagram Post</h3>
                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-indigo-100 rounded-lg">
                        <Instagram className="text-indigo-600" size={24} />
                      </div>
                      <div className="flex-1">
                        <a
                          href={submission.posting.instagram_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          View on Instagram
                        </a>
                        {submission.posting.posted_at && (
                          <p className="text-sm text-gray-600 mt-1">
                            Posted on {new Date(submission.posting.posted_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Metrics */}
              {submission.metrics.last_synced_at && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { label: 'Impressions', value: submission.metrics.lifetime.impressions, icon: TrendingUp },
                      { label: 'Reach', value: submission.metrics.lifetime.reach, icon: Users },
                      { label: 'Likes', value: submission.metrics.lifetime.likes, icon: CheckCircle },
                      { label: 'Engagement', value: submission.metrics.lifetime.engagement, icon: TrendingUp },
                    ].map((metric) => (
                      <div key={metric.label} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center space-x-2 mb-2">
                          <metric.icon size={16} className="text-gray-600" />
                          <p className="text-sm text-gray-600">{metric.label}</p>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{metric.value.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Last synced: {new Date(submission.metrics.last_synced_at).toLocaleString()}
                  </p>
                </div>
              )}

              {/* Approval/Rejection Messages */}
              {submission.approvals && submission.approvals.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Approval History</h3>
                  <div className="space-y-3">
                    {submission.approvals.map((approval, idx) => (
                      <div 
                        key={idx} 
                        className={`rounded-lg p-4 border ${
                          approval.decision === 'APPROVED' 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                                approval.decision === 'APPROVED'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {approval.decision}
                              </span>
                              <span className="text-sm text-gray-600">
                                {approval.step === 'APPROVAL_1' ? 'Step 1' : 'Step 2'}
                              </span>
                            </div>
                            {approval.reason && (
                              <p className="text-gray-700 mb-2">
                                <span className="font-medium">
                                  {approval.decision === 'APPROVED' ? 'Comment:' : 'Reason:'}
                                </span>{' '}
                                {approval.reason}
                              </p>
                            )}
                            {approval.decided_at && (
                              <p className="text-xs text-gray-500">
                                {new Date(approval.decided_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'timeline' && (
            <div data-testid="timeline-content">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Activity Timeline</h3>
              <div className="space-y-3 sm:space-y-4">
                {submission.timeline.map((event, idx) => (
                  <div key={idx} className="flex items-start gap-3 sm:gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Clock size={16} className="text-blue-600 sm:w-5 sm:h-5" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm sm:text-base break-words">{event.event_type}</p>
                          {event.user_name && (
                            <p className="text-xs sm:text-sm text-gray-600">by {event.user_name}</p>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 whitespace-nowrap">
                          {new Date(event.timestamp).toLocaleString()}
                        </p>
                      </div>
                      {event.details && (
                        <div className="mt-2">
                          <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto whitespace-pre-wrap break-words max-w-full">
                            {JSON.stringify(event.details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'creative' && (
            <div data-testid="creative-content" className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Package for Final Approval</h3>
              
              {/* Show current packaged asset info if exists */}
              {firstPackagedAsset && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 font-medium mb-2">Current Packaged Asset</p>
                  <p className="text-sm text-blue-700">
                    {firstPackagedAsset.original_filename} ({(firstPackagedAsset.size_bytes / 1024 / 1024).toFixed(2)} MB)
                  </p>
                  <p className="text-xs text-blue-600 mt-2">You can upload a new file below to replace it</p>
                </div>
              )}
              
              {/* Checkbox to use original asset */}
              {firstAsset && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useOriginalAsset}
                      onChange={(e) => {
                        setUseOriginalAsset(e.target.checked);
                        if (e.target.checked) {
                          // Clear any manually selected file
                          setPackageFile(null);
                          setPackagePreview(null);
                        }
                      }}
                      className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-900">
                        Use original submission file as packaged asset
                      </span>
                      <div className="mt-2 text-xs text-gray-700 bg-white rounded p-2 border border-blue-100">
                        <p className="font-medium mb-1">Original File:</p>
                        <p className="text-gray-600">📁 {firstAsset.original_filename}</p>
                        <p className="text-gray-500 mt-1">
                          {firstAsset.mime_type} • {formatBytes(firstAsset.size_bytes)}
                        </p>
                      </div>
                    </div>
                  </label>
                </div>
              )}
              
              {/* Upload Packaged Asset - Always visible */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Packaged Asset <span className="text-red-500">*</span>
                  {useOriginalAsset && (
                    <span className="ml-2 text-xs text-blue-600 font-normal">
                      (Using original file - uncheck above to upload a different file)
                    </span>
                  )}
                </label>
                
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    useOriginalAsset 
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
                      : 'border-gray-300 cursor-pointer hover:border-blue-500'
                  }`}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    if (useOriginalAsset) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const files = e.dataTransfer.files;
                    if (files.length > 0) handlePackageFileSelect(files[0]);
                  }}
                  onClick={() => !useOriginalAsset && fileInputRef.current?.click()}
                >
                  {!packageFile ? (
                    <div>
                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <p className="mt-2 text-sm text-gray-600">
                        Drag and drop your packaged file here, or click to browse
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        Supported: Images, Videos, Audio (MP3, WAV), PDF, ZIP files
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm font-medium text-gray-900">{packageFile.name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {(packageFile.size / 1024 / 1024).toFixed(2)} MB • {packageFile.type}
                      </p>
                      {packagePreview && packageFile.type.startsWith('image/') && (
                        <img src={packagePreview} alt="Preview" className="mt-4 max-h-48 mx-auto rounded" />
                      )}
                      {packagePreview && packageFile.type.startsWith('video/') && (
                        <video src={packagePreview} className="mt-4 max-h-48 mx-auto rounded" controls />
                      )}
                    </div>
                  )}
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      handlePackageFileSelect(e.target.files[0]);
                    }
                  }}
                  className="hidden"
                  accept="image/*,video/*,audio/*,application/pdf,application/zip"
                />
              </div>

              {/* Creative Type - NEW Required Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Creative Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={creativeType}
                  onChange={(e) => setCreativeType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select creative type...</option>
                  <option value="Reel">Reel</option>
                  <option value="Single Photo Post">Single Photo Post</option>
                  <option value="Carousel">Carousel</option>
                  <option value="Video Post">Video Post</option>
                  <option value="Story">Story</option>
                </select>
              </div>

              {/* Caption - Now REQUIRED */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Caption <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={packageCaption}
                  onChange={(e) => setPackageCaption(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter social media caption for the packaged asset..."
                  required
                />
              </div>

              {/* Suggested Hashtags - NEW Optional Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Suggested Hashtags / Keywords (Optional)
                </label>
                <textarea
                  value={suggestedHashtags}
                  onChange={(e) => setSuggestedHashtags(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter hashtags and keywords (e.g., #marketing #socialmedia #campaign)"
                />
                <p className="text-xs text-gray-500 mt-1">Suggest hashtags or keywords for the post</p>
              </div>

              {/* Proposed Posting Date - NEW Required Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Proposed Posting Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={proposedPostingDate}
                  onChange={(e) => setProposedPostingDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Suggest when this should be posted</p>
              </div>

              {/* Tag - NEW Optional Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tag (Optional)
                </label>
                <input
                  type="text"
                  value={packageTag}
                  onChange={(e) => setPackageTag(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tag people or team members (e.g., @john, @marketing_team)"
                />
                <p className="text-xs text-gray-500 mt-1">Tag persons or team members associated with this creative</p>
              </div>

              {/* Collaborators - Editable in Creative tab */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Collaborators (Optional)
                </label>
                <textarea
                  value={packageCollaborators}
                  onChange={(e) => setPackageCollaborators(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter collaborators (one per line or as free text)"
                />
                <p className="text-xs text-gray-500 mt-1">Add collaborators for this submission</p>
              </div>

              {/* Upload Progress */}
              {uploadStatus === 'uploading' && (
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Uploading packaged asset...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Success Message */}
              {uploadStatus === 'confirmed' && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <p className="text-green-800 font-medium">✓ Packaged asset uploaded successfully!</p>
                  <p className="text-green-700 text-sm mt-1">You can now submit for Step 2 approval using the button at the top.</p>
                </div>
              )}

              {/* Error Message */}
              {uploadError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-red-800 font-medium">Error</p>
                  <p className="text-red-700 text-sm mt-1">{uploadError}</p>
                </div>
              )}

              {/* Upload/Save Button */}
              <button
                onClick={handlePackageUpload}
                disabled={(() => {
                  // If uploading, always disable
                  if (uploadStatus === 'uploading') return true;
                  
                  // If no file AND no existing packaged asset AND not using original asset, disable (need initial file)
                  if (!packageFile && !firstPackagedAsset && !useOriginalAsset) return true;
                  
                  // For REJECTED submissions with packaged asset, NEVER disable
                  if (submission?.status === 'REJECTED' && firstPackagedAsset) return false;
                  
                  // For other statuses, disable if nothing changed
                  if (firstPackagedAsset && !packageFile) {
                    const noChanges = 
                      packageCaption === (submission?.caption || '') &&
                      packageCollaborators === (submission?.collaborators_text || '') &&
                      creativeType === (submission?.creative_type || '') &&
                      suggestedHashtags === (submission?.suggested_hashtags || '') &&
                      proposedPostingDate === (submission?.proposed_posting_date || '') &&
                      packageTag === (submission?.tag || '');
                    return noChanges;
                  }
                  
                  // Otherwise, enabled (has file or has changes)
                  return false;
                })()}
                className={`w-full py-3 px-6 rounded-md font-medium transition-colors ${
                  uploadStatus === 'uploading' ? 'bg-gray-300 text-gray-500 cursor-not-allowed' :
                  // Show blue if: has new file, is using original asset, is REJECTED with asset, or has changes
                  (packageFile || 
                   useOriginalAsset ||
                   (submission?.status === 'REJECTED' && firstPackagedAsset) ||
                   (firstPackagedAsset && (
                    packageCaption !== (submission?.caption || '') ||
                    packageCollaborators !== (submission?.collaborators_text || '') ||
                    creativeType !== (submission?.creative_type || '') ||
                    suggestedHashtags !== (submission?.suggested_hashtags || '') ||
                    proposedPostingDate !== (submission?.proposed_posting_date || '') ||
                    packageTag !== (submission?.tag || '')
                  )))
                    ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {uploadStatus === 'uploading' ? 'Uploading...' : firstPackagedAsset ? 'Update Packaged Asset' : 'Upload Packaged Asset'}
              </button>
            </div>
          )}

          {activeTab === 'posting' && (
            <div data-testid="posting-content" className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Record Instagram Posting</h3>
              
              {submission.posting?.instagram_url ? (
                // Read-only view when posting is already recorded
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 font-medium mb-3">✓ Posting recorded successfully!</p>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Instagram URL
                        </label>
                        <a 
                          href={submission.posting.instagram_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline break-all"
                        >
                          {submission.posting.instagram_url}
                        </a>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Posted At
                        </label>
                        <p className="text-gray-900">
                          {new Date(submission.posting.posted_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800 text-sm">
                      The posting has been recorded. Contact an administrator if you need to update this information.
                    </p>
                  </div>
                </div>
              ) : (
                // Editable form when status is APPROVAL_2_APPROVED
                <form onSubmit={handlePostingSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Instagram URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      value={instagramUrl}
                      onChange={(e) => setInstagramUrl(e.target.value)}
                      placeholder="https://www.instagram.com/p/... or https://www.instagram.com/reel/..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter the full Instagram post or reel URL
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Posted At <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={postedAt}
                      onChange={(e) => setPostedAt(e.target.value)}
                      max={(() => {
                        const now = new Date();
                        return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
                          .toISOString()
                          .slice(0, 16);
                      })()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Select when the content was posted (cannot be in the future)
                    </p>
                  </div>
                  
                  {postingError && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                      <p className="text-red-800 font-medium">Error</p>
                      <p className="text-red-700 text-sm mt-1">{postingError}</p>
                    </div>
                  )}
                  
                  {postingStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-4">
                      <p className="text-green-800 font-medium">✓ Posting recorded successfully!</p>
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={postingStatus === 'submitting'}
                    className={`w-full py-3 px-6 rounded-md font-medium transition-colors ${
                      postingStatus === 'submitting'
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {postingStatus === 'submitting' ? 'Recording...' : 'Record Posting'}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Image Preview Modal */}
      {imagePreviewOpen && previewAsset && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setImagePreviewOpen(false)}
          data-testid="image-preview-modal"
        >
          <div 
            className="relative bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 bg-white border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {previewType === 'original' ? 'Original Asset' : 'Packaged Asset'}
                </h3>
                <p className="text-sm text-gray-600">{previewAsset.original_filename}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => openInNewTab(previewAsset)}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                  data-testid="open-in-new-tab"
                >
                  <ExternalLink size={16} className="mr-2" />
                  Open in New Tab
                </button>
                <button
                  onClick={() => setImagePreviewOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition"
                  data-testid="close-preview"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6">
              <img 
                src={previewUrl} 
                alt={previewAsset.original_filename}
                className="w-full h-auto rounded-lg"
              />
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Type</p>
                    <p className="font-medium text-gray-900">{previewAsset.mime_type}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Size</p>
                    <p className="font-medium text-gray-900">{formatBytes(previewAsset.size_bytes)}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-600">Storage Key</p>
                    <p className="font-medium text-gray-900 font-mono text-xs break-all">{previewAsset.storage_key}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Preview Modal */}
      {videoPreviewOpen && previewAsset && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setVideoPreviewOpen(false)}
          data-testid="video-preview-modal"
        >
          <div 
            className="relative bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 bg-white border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {previewType === 'original' ? 'Original Asset' : 'Packaged Asset'}
                </h3>
                <p className="text-sm text-gray-600">{previewAsset.original_filename}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => openInNewTab(previewAsset)}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                  data-testid="open-video-in-new-tab"
                >
                  <ExternalLink size={16} className="mr-2" />
                  Open in New Tab
                </button>
                <button
                  onClick={() => setVideoPreviewOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition"
                  data-testid="close-video-preview"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6">
              <video 
                src={previewUrl}
                controls
                autoPlay
                className="w-full h-auto rounded-lg bg-black"
                data-testid="video-player"
              >
                Your browser does not support the video tag.
              </video>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Type</p>
                    <p className="font-medium text-gray-900">{previewAsset.mime_type}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Size</p>
                    <p className="font-medium text-gray-900">{formatBytes(previewAsset.size_bytes)}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-600">Storage Key</p>
                    <p className="font-medium text-gray-900 font-mono text-xs break-all">{previewAsset.storage_key}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowSubmitConfirm(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-2">Submit to Creative Agency?</h3>
            <p className="text-gray-600 mb-6">
              This will send your submission to the Creative Agency for review. They will either accept it or request edits.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowSubmitConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitForApproval}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit to Agency'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowApproveModal(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-2">Approve Submission?</h3>
            <p className="text-gray-600 mb-1">
              <strong>{submission?.title}</strong>
            </p>
            <p className="text-sm text-gray-600 mb-4">
              This will approve the submission and move it to the next stage.
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comment (Optional)
              </label>
              <textarea
                value={approveComment}
                onChange={(e) => setApproveComment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                rows={3}
                placeholder="Add any comments or notes about this approval..."
                data-testid="approve-comment-input"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setApproveComment('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                disabled={processing}
                data-testid="confirm-approve"
              >
                {processing ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowRejectModal(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-2">Reject Submission?</h3>
            <p className="text-gray-600 mb-1">
              <strong>{submission?.title}</strong>
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejection:
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mb-6"
              rows={4}
              placeholder="Explain why this submission is being rejected..."
              data-testid="reject-reason-input"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                disabled={processing || !rejectReason.trim()}
                data-testid="confirm-reject"
              >
                {processing ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Agency Accept Modal */}
      {showAgencyAcceptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Accept Creative</h3>
            <p className="text-gray-600 mb-4">
              Accept this creative and move it to the packaging stage?
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comments (Optional)
              </label>
              <textarea
                value={agencyComments}
                onChange={(e) => setAgencyComments(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Add any comments or feedback..."
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowAgencyAcceptModal(false);
                  setAgencyComments('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleAgencyAccept}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                disabled={processing}
                data-testid="confirm-agency-accept"
              >
                {processing ? 'Accepting...' : 'Accept'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Agency Request Edits Modal */}
      {showAgencyEditRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Request Edits</h3>
            <p className="text-gray-600 mb-4">
              Explain what changes are needed:
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comments <span className="text-red-500">*</span>
              </label>
              <textarea
                value={agencyComments}
                onChange={(e) => setAgencyComments(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Describe what needs to be changed..."
                required
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowAgencyEditRequestModal(false);
                  setAgencyComments('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleAgencyRequestEdits}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                disabled={processing || !agencyComments.trim()}
                data-testid="confirm-agency-request-edits"
              >
                {processing ? 'Sending...' : 'Request Edits'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionDetailPage;
